import { DatePipe, DecimalPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { finalize } from 'rxjs/operators';
import { ProductCostsApiService } from '../product-costs.api.service';
import { TimelineSegment } from '../product-costs.model';
import {
	buildDayScaleWithMonth,
	buildMonthSegments,
	buildTimelineSegments,
	countDaysInRange,
	DAY_SCALE_THRESHOLD_DAYS,
} from './product-cost-timeline.utils';

@Component({
	selector: 'app-product-cost-timeline',
	standalone: true,
	imports: [DatePipe, DecimalPipe, MatProgressBar, MatTooltipModule, NgClass],
	templateUrl: './product-cost-timeline.component.html',
	styleUrl: './product-cost-timeline.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductCostTimelineComponent {
	readonly costId = input.required<number>();
	readonly rangeStart = input.required<Date>();
	readonly rangeEnd = input.required<Date>();

	readonly segments = signal<TimelineSegment[]>([]);
	readonly isLoading = signal(false);

	readonly daysInRange = computed(() => {
		const start = this.rangeStart();
		const end = this.rangeEnd();
		return Math.max(1, countDaysInRange(start, end));
	});

	readonly dayScaleWithMonth = computed(() => {
		const start = this.rangeStart();
		const end = this.rangeEnd();
		return buildDayScaleWithMonth(start, end);
	});

	readonly showDayScale = computed(() => this.daysInRange() <= DAY_SCALE_THRESHOLD_DAYS);

	readonly monthSegments = computed(() => {
		const start = this.rangeStart();
		const end = this.rangeEnd();
		return buildMonthSegments(start, end);
	});

	readonly monthBoundaryPositions = computed(() => {
		const monthSegs = this.monthSegments();
		const positions: number[] = [0];
		let cum = 0;
		for (const seg of monthSegs) {
			cum += seg.widthPercent;
			positions.push(cum);
		}
		return positions;
	});

	readonly dayScaleWidthPercent = computed(() => 100 / this.daysInRange());

	private readonly productCostsApiService = inject(ProductCostsApiService);
	private readonly destroyRef = inject(DestroyRef);

	constructor() {
		effect(
			() => {
				const id = this.costId();
				this.rangeStart();
				this.rangeEnd();
				this.loadPricesForCost(id);
			},
			{ allowSignalWrites: true },
		);
	}

	private loadPricesForCost(costId: number): void {
		const start = this.rangeStart();
		const end = this.rangeEnd();
		this.isLoading.set(true);
		this.productCostsApiService
			.getProductCost(costId)
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				finalize(() => this.isLoading.set(false)),
			)
			.subscribe((data) => {
				const timelineSegments = buildTimelineSegments(data.prices, start, end);
				this.segments.set(timelineSegments);
			});
	}
}
